import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useEditMode,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions — income tiers (property-specific; generators fill)
// ============================================================================

const tier1Def: SlotGroupDefinition = {
  label: 'Income Tier 1',
  fields: {
    bedroom: {
      label: 'Bedroom Label',
      type: 'text',
      defaultValue: 'One-Bedroom Rent',
    },
    rent: {
      label: 'Monthly Rent',
      type: 'text',
      defaultValue: '$1,024 per month',
    },
    households: {
      label: 'Household Income Ranges',
      type: 'richtext',
      defaultValue:
        '<ul><li>1-Person Household: $44,000 – $48,100</li><li>2-Person Household: $44,000 – $54,950</li></ul>',
    },
  },
  generator: {
    prompt:
      'Income-restricted rent and min/max gross annual household income ranges for the smallest bedroom size offered under this property’s affordable program (e.g. one-bedroom). If this property has no income-restricted units, leave empty.',
    context:
      'Tier 1 of up to 3 on the Income Restricted page. bedroom = unit size label ending in “Rent”; rent = monthly rent amount (or “Contact office”); households = bullet list of household-size income ranges. Do not invent figures — use published program limits for this property, or leave empty.',
  },
};

const tier2Def: SlotGroupDefinition = {
  label: 'Income Tier 2',
  fields: {
    bedroom: {
      label: 'Bedroom Label',
      type: 'text',
      defaultValue: 'Two-Bedroom Rent',
    },
    rent: {
      label: 'Monthly Rent',
      type: 'text',
      defaultValue: '$1,193 per month',
    },
    households: {
      label: 'Household Income Ranges',
      type: 'richtext',
      defaultValue:
        '<ul><li>2-Person Household: $52,200 – $54,950</li><li>3-Person Household: $52,200 – $61,800</li><li>4-Person Household: $52,200 – $68,650</li></ul>',
    },
  },
  generator: {
    prompt:
      'Income-restricted rent and min/max gross annual household income ranges for the next bedroom size under this property’s affordable program (e.g. two-bedroom). Leave empty if not offered.',
    context:
      'Tier 2 of up to 3 on the Income Restricted page. Must be a different bedroom size than tier 1. Same field roles as tier 1. Leave empty when unused.',
  },
};

const tier3Def: SlotGroupDefinition = {
  label: 'Income Tier 3',
  fields: {
    bedroom: {
      label: 'Bedroom Label',
      type: 'text',
      defaultValue: '',
    },
    rent: {
      label: 'Monthly Rent',
      type: 'text',
      defaultValue: '',
    },
    households: {
      label: 'Household Income Ranges',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'Income-restricted rent and min/max gross annual household income ranges for a third bedroom size if offered. Leave empty when not needed.',
    context:
      'Tier 3 of up to 3 on the Income Restricted page. Optional — leave empty unless this property publishes a third income-restricted bedroom size. Different from tiers 1 and 2.',
  },
};

// ============================================================================
// Helpers
// ============================================================================

const BODY_CLS =
  'font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)]' +
  ' [&_p]:mb-4 [&_p:last-child]:mb-0' +
  ' [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-0 [&_li]:mb-1.5' +
  ' [&_a]:text-primary [&_a]:underline';

const HEADING_CLS =
  'font-heading text-xl sm:text-2xl font-normal italic text-[var(--vibe-color-text)] mb-3';

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function IncomeTierBody({
  bedroom,
  rent,
  households,
  showPlaceholder,
}: {
  bedroom: string;
  rent: string;
  households: string;
  showPlaceholder: boolean;
}) {
  const hasContent = Boolean(bedroom?.trim() || rent?.trim() || households?.trim());
  if (!hasContent && !showPlaceholder) return null;

  const rentLabel = [bedroom?.trim() || 'Bedroom Rent', rent?.trim()].filter(Boolean).join(': ');

  if (!hasContent) {
    return (
      <p className="font-body text-sm text-[var(--vibe-color-text-muted)] italic m-0">
        Optional income tier — generate or enter rent and household ranges.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="font-body text-base sm:text-lg font-semibold text-[var(--vibe-color-text)] m-0">
        {rentLabel}
      </p>
      {households?.trim() ? (
        <div
          className={
            'font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)]' +
            ' [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-0 [&_li]:mb-1.5'
          }
          dangerouslySetInnerHTML={{ __html: households }}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function IncomeRestrictedContent() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const incomeRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  const editMode = useEditMode();
  const showPlaceholders = Boolean(editMode?.isEditMode);

  const tier1 = useSlotGroup('incomerestrictedcontent.tier_1', tier1Def);
  const tier2 = useSlotGroup('incomerestrictedcontent.tier_2', tier2Def);
  const tier3 = useSlotGroup('incomerestrictedcontent.tier_3', tier3Def);

  const phone = useContentToken('incomerestrictedcontent.phone');
  const phoneDisplay = phone ? formatDisplayPhone(phone) : '(555) 555-5555';
  const telHref = phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : undefined;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up relative py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      {/* Soft atmospheric wash — not a flat single-color plane */}
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        aria-hidden
        style={{
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 10% 0%, color-mix(in srgb, var(--vibe-color-primary) 8%, transparent), transparent 55%),' +
            'radial-gradient(ellipse 60% 40% at 90% 100%, color-mix(in srgb, var(--vibe-color-primary) 6%, transparent), transparent 50%)',
        }}
      />

      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="incomerestrictedcontent.intro"
          as="h2"
          className="font-heading text-2xl sm:text-3xl font-normal italic text-[var(--vibe-color-text)] mb-10 md:mb-12 text-balance"
        />

        <div className="space-y-10 md:space-y-12">
          <div>
            <RichTextContent
              path="incomerestrictedcontent.eligibility_heading"
              as="h3"
              className={HEADING_CLS}
            />
            <RichTextContent
              path="incomerestrictedcontent.eligibility_body"
              as="div"
              className={BODY_CLS}
            />
          </div>

          <div>
            <RichTextContent
              path="incomerestrictedcontent.utilities_heading"
              as="h3"
              className={HEADING_CLS}
            />
            <RichTextContent
              path="incomerestrictedcontent.utilities_body"
              as="div"
              className={BODY_CLS}
            />
          </div>

          <div>
            <RichTextContent
              path="incomerestrictedcontent.important_heading"
              as="h3"
              className={HEADING_CLS}
            />
            <RichTextContent
              path="incomerestrictedcontent.important_body"
              as="div"
              className={BODY_CLS}
            />
          </div>
        </div>

        {/* Income limits */}
        <div ref={incomeRef} className="reveal-fade-up mt-14 md:mt-16 pt-10 md:pt-12 border-t border-[color-mix(in_srgb,var(--vibe-color-text)_12%,transparent)]">
          <RichTextContent
            path="incomerestrictedcontent.income_heading"
            as="h3"
            className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-8 not-italic tracking-wide"
          />

          <div className="space-y-8 md:space-y-10">
            <EditableSlotGroup
              groupPath="incomerestrictedcontent.tier_1"
              definition={tier1Def}
              className="block"
            >
              <IncomeTierBody
                bedroom={tier1.bedroom || ''}
                rent={tier1.rent || ''}
                households={tier1.households || ''}
                showPlaceholder={showPlaceholders}
              />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="incomerestrictedcontent.tier_2"
              definition={tier2Def}
              className="block"
            >
              <IncomeTierBody
                bedroom={tier2.bedroom || ''}
                rent={tier2.rent || ''}
                households={tier2.households || ''}
                showPlaceholder={showPlaceholders}
              />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="incomerestrictedcontent.tier_3"
              definition={tier3Def}
              className="block"
            >
              <IncomeTierBody
                bedroom={tier3.bedroom || ''}
                rent={tier3.rent || ''}
                households={tier3.households || ''}
                showPlaceholder={showPlaceholders}
              />
            </EditableSlotGroup>
          </div>
        </div>

        {/* Contact CTA */}
        <div
          ref={ctaRef}
          className="reveal-fade-up mt-14 md:mt-16 pt-10 md:pt-12 border-t border-[color-mix(in_srgb,var(--vibe-color-text)_12%,transparent)]"
        >
          <p className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)] m-0">
            <RichTextContent
              path="incomerestrictedcontent.cta_before"
              as="span"
              className="[&_p]:inline [&_p]:m-0"
            />{' '}
            {telHref ? (
              <a
                href={telHref}
                className="dniphone dniphonehref font-semibold text-primary underline underline-offset-2 whitespace-nowrap"
              >
                <EditableToken path="incomerestrictedcontent.phone" as="span">
                  {phoneDisplay}
                </EditableToken>
              </a>
            ) : (
              <EditableToken
                path="incomerestrictedcontent.phone"
                as="span"
                className="font-semibold text-primary underline underline-offset-2 whitespace-nowrap"
              >
                {phoneDisplay}
              </EditableToken>
            )}{' '}
            <RichTextContent
              path="incomerestrictedcontent.cta_after"
              as="span"
              className="[&_p]:inline [&_p]:m-0"
            />
          </p>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'IncomeRestrictedContent',
  displayName: 'Income Restricted Content',
  tokens: {
    'incomerestrictedcontent.intro': {
      label: 'Intro Title',
      type: 'richtext',
      defaultValue:
        '<p>Income-Restricted Apartments at <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'incomerestrictedcontent.eligibility_heading': {
      label: 'Eligibility Heading',
      type: 'richtext',
      defaultValue: '<p>Eligibility</p>',
    },
    'incomerestrictedcontent.eligibility_body': {
      label: 'Eligibility Body',
      type: 'richtext',
      defaultValue:
        '<p>Eligibility is determined by household size and gross annual household income. For the most up-to-date income limits and maximum allowable rents, please contact our office. Minimum income requirements do not apply to households applying with a housing voucher. Applicants with Housing Choice Vouchers are encouraged to apply.</p>',
      generator: {
        prompt:
          'Short eligibility copy for this property’s income-restricted or affordable apartments: household size, gross annual income, contacting the office for current limits, and that voucher holders are welcome (no minimum income with a voucher).',
        context:
          'Eligibility block on the Income Restricted page, above Utilities and the income tables.',
        length: '3–5 sentences',
      },
    },
    'incomerestrictedcontent.utilities_heading': {
      label: 'Utilities Heading',
      type: 'richtext',
      defaultValue: '<p>Utilities</p>',
    },
    'incomerestrictedcontent.utilities_body': {
      label: 'Utilities Body',
      type: 'richtext',
      defaultValue:
        '<p>A utility allowance for electricity, gas, and water/sewer (as applicable by apartment and program) will be factored into your rent calculation.</p>',
      generator: {
        prompt:
          'One short paragraph on how utility allowances apply to income-restricted rent at this property. Stay general if specifics are unknown.',
        context: 'Utilities block on the Income Restricted page, between Eligibility and Important Information.',
        length: '1–2 sentences',
      },
    },
    'incomerestrictedcontent.important_heading': {
      label: 'Important Information Heading',
      type: 'richtext',
      defaultValue: '<p>Important Information</p>',
    },
    'incomerestrictedcontent.important_body': {
      label: 'Important Information Body',
      type: 'richtext',
      defaultValue:
        '<p>Apartments may include affordable or FHA-compliant accessibility features; please inquire about available accommodations.</p><p>Residents are responsible for any utilities not included in the rent, as well as phone and cable services, as applicable to the home.</p>',
      generator: {
        prompt:
          'Two short notes: accessibility/accommodations inquiry, and resident responsibility for utilities not included in rent (plus phone/cable as applicable).',
        context: 'Important Information block on the Income Restricted page, above the income tables.',
        length: '2 short paragraphs',
      },
    },
    'incomerestrictedcontent.income_heading': {
      label: 'Income Limits Heading',
      type: 'richtext',
      defaultValue: '<p>Minimum and Maximum Allowable Household Income</p>',
    },
    'incomerestrictedcontent.cta_before': {
      label: 'Contact CTA (before phone)',
      type: 'richtext',
      defaultValue: '<p>Please contact the leasing office at</p>',
    },
    'incomerestrictedcontent.cta_after': {
      label: 'Contact CTA (after phone)',
      type: 'richtext',
      defaultValue:
        '<p>for more information and to learn more about the income-restricted application process.</p>',
    },
    'incomerestrictedcontent.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
  },
  slotGroups: {
    'incomerestrictedcontent.tier_1': tier1Def,
    'incomerestrictedcontent.tier_2': tier2Def,
    'incomerestrictedcontent.tier_3': tier3Def,
  },
};

(IncomeRestrictedContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default IncomeRestrictedContent;
